import axios, { AxiosError } from 'axios';

import { selectAppliedAddOns } from '@lib/core/retailers/selectors/retailerLocation';
import { ENV_BACKEND_DOMAIN, REQUEST_ERROR_FORBIDDEN_MESSAGE, REQUEST_METHODS } from '@lib/core/service/consts';
import backendApiUrls from '@lib/core/service/requests/backend_api_urls';
import { parseError } from '@lib/core/service/requests/parseError';
import { RequestArgs } from '@lib/core/service/requests/request';
import { store } from '@lib/core/service/store';
import { prependBasename } from '@lib/core/service/utils';
import { history } from '@lib/core/service/utils/Navigator';
import { selectAuthToken, selectUserRefreshToken } from '@lib/core/users/selectors/auth';
import { actionResetAuthSlice, actionSaveAccessToken } from '@lib/core/users/slices/auth';
import { PAGES } from '@lib/tools/views/urls';

export const handleAxiosErrorScenarios = async ({
  error,
  requestParams,
  requestFunction,
  headers,
}: {
  error: AxiosError;
  requestParams: RequestArgs;
  requestFunction: (args: RequestArgs) => any;
  headers: Record<string, string>;
}) => {
  if (!axios.isAxiosError(error)) {
    return Promise.reject(parseError(error));
  }

  const state = store.getState();
  const authToken = selectAuthToken(state);
  const refreshToken = selectUserRefreshToken(state);
  const { status, data, config: errorResponseConfig } = error.response || {};

  if (axios.isCancel(error)) {
    return Promise.reject(parseError(error));
  }

  const { isNoEmailAddon } = selectAppliedAddOns(state);
  const isNoEmailAddonError = isNoEmailAddon && errorResponseConfig?.url?.includes('send_confirm_email');

  const vhApiErrorFormattedData = data as {
    detail?: string;
  };

  if (status === 401 && refreshToken && !isNoEmailAddonError) {
    try {
      const refreshTokenResponse = await axios({
        baseURL: ENV_BACKEND_DOMAIN,
        data: {
          refresh: refreshToken,
        },
        headers,
        method: REQUEST_METHODS.POST,
        url: backendApiUrls.refreshTokenUrl,
      });
      const newAccessToken = refreshTokenResponse?.data?.access;
      if (newAccessToken) {
        store.dispatch(actionSaveAccessToken(newAccessToken));
        return requestFunction({ ...requestParams, authToken: newAccessToken });
      }
      return store.dispatch(actionResetAuthSlice());
    } catch (e: AxiosError | unknown) {
      if (e instanceof AxiosError) {
        if (authToken && (e?.response?.config?.url === backendApiUrls.refreshTokenUrl || e?.response?.status === 401)) {
          store.dispatch(actionResetAuthSlice());
        }
        return Promise.reject(parseError(e));
      }

      console.error('Unexpected error:', e);
      return Promise.reject(parseError(null));
    }
  }

  if (status === 403) {
    if (vhApiErrorFormattedData.detail === REQUEST_ERROR_FORBIDDEN_MESSAGE) {
      history.push(prependBasename(PAGES.error.forbidden));
    }
  }

  if (status >= 500) {
    history.push(prependBasename(PAGES.error.badRequest));
  }

  return Promise.reject(parseError(error));
};
